import { useState, useEffect, useCallback } from 'react'
import GenericTable from '../GenericTable'
import { d1Query, d1Insert, d1Update, d1Delete } from '../../services/d1Service'
import type { QuickButton, StatusMessage } from '../../types'

interface Props { eiin: string; onStatus: (msg: StatusMessage) => void }

export default function QuickButtonsTab({ eiin, onStatus }: Props) {
  const [items, setItems] = useState<QuickButton[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Partial<QuickButton> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try { setItems(await d1Query<QuickButton>(`SELECT * FROM quick_buttons WHERE institution_eiin = ? ORDER BY sort_order ASC`, [eiin])) }
    catch (e: any) { onStatus({ type: 'error', text: e.message }) }
    finally { setLoading(false) }
  }, [eiin])

  useEffect(() => { load() }, [load])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editing) return
    try {
      const rec = { ...editing, institution_eiin: eiin }
      if ((editing as any).id) await d1Update('quick_buttons', (editing as any).id, rec)
      else await d1Insert('quick_buttons', rec)
      onStatus({ type: 'success', text: 'Quick button saved!' })
      setEditing(null); load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  return (
    <GenericTable
      title="Quick Buttons" icon="⚡"
      items={items} loading={loading} editingItem={editing}
      columns={[
        { key: 'title_bn', label: 'Title (BN)', width: '200px' },
        { key: 'title_en', label: 'Title (EN)', width: '200px' },
        { key: 'action_type', label: 'Action Type', width: '140px' },
        { key: 'bg_color', label: 'Color', width: '80px', render: (v) => <span style={{ display: 'inline-block', width: 20, height: 20, borderRadius: 4, background: v }} /> }
      ]}
      formFields={[
        { key: 'title_bn', label: 'Title (BN)', required: true },
        { key: 'title_en', label: 'Title (EN)', required: true },
        { key: 'action_type', label: 'Action Type', required: true },
        { key: 'link_url', label: 'Link URL' },
        { key: 'bg_color', label: 'BG Color (hex)', required: true },
        { key: 'sort_order', label: 'Sort Order', type: 'number' }
      ]}
      onAdd={() => setEditing({ title_bn: '', title_en: '', action_type: 'internal_results', bg_color: '#007bff', sort_order: 1 })}
      onEdit={(item) => setEditing({ ...item })}
      onDelete={async (id) => { try { await d1Delete('quick_buttons', id); onStatus({ type: 'success', text: 'Deleted!' }); load() } catch (e: any) { onStatus({ type: 'error', text: e.message }) } }}
      onSave={handleSave}
      onCancel={() => setEditing(null)}
      onFieldChange={(key, val) => setEditing(prev => prev ? { ...prev, [key]: val } : prev)}
    />
  )
}
